import React from 'react';
import loadable from '@loadable/component';
import { Typography, Violator } from '@hpstellar/core';
import { Typography as TypographyVisId, Tooltip } from '@hpstellar/core/visId';
import Tag from '../../../ui/components/tag';

import useTemplateTheme from '../../../hooks/useTemplateTheme';
import withPageComponents from '../../../page/components/with-page-components';
import ConfigureRadio from './configure-radio';
import ConfigureCheckbox from './configure-checkbox';
import { Helpers } from '../../../core/src/helpers';
import AlertMessage from '../../../shared/components/alert-message';
import DynamicIcon from '../../../ui/components/dynamic-icon';

const MobileConfigureOption = loadable(() => Helpers.retryFunc(() => import('./mobile-configure-option')));

import './configure-option.less';

export default withPageComponents(
    ({
        baseConfig = {},
        device,
        ctoProductInitial,
        ctoPrice,
        GMPoints,
        trackCustomMetric,
        ctoSettings,
        v2,
        innerWidth,
    }) => {
        const { specCategories = [] } = baseConfig;
        const { disableMobileCTOEnhancements, disableIcons } = ctoSettings || {};
        const { css, typography } = useTemplateTheme(['typography']);
        const { primaryColor } = typography || {};
        const violatorStyle = primaryColor ? { color: primaryColor } : {};
        const inputVariant = device === 'mobile' ? 'secondary' : undefined;
        if (device === 'mobile' && disableMobileCTOEnhancements) {
            return (
                <MobileConfigureOption
                    specCategories={specCategories}
                    ctoProductInitial={ctoProductInitial}
                    ctoPrice={ctoPrice}
                    GMPoints={GMPoints}
                    trackCustomMetric={trackCustomMetric}
                />
            );
        }

        return (
            <div className={'spec-categories'}>
                {specCategories.map((spec, i) => {
                    const {
                        title,
                        description,
                        type,
                        optionitems,
                        display,
                        errorText,
                        hasNoneSelection,
                        infoText,
                        warningText,
                        guidingText,
                        path,
                        columnSize,
                        promoMessage,
                        image,
                    } = spec;

                    const renderIcon = !disableIcons && image;
                    const colorSwatchProps = device === 'mobile' ? { shape: 'square' } : {};
                    return display === 'true' ? (
                        <div
                            className={`configure-option${v2 ? ' configure-option-v2' : ''}`}
                            key={`${title}-${i}`}
                            data-path={path}
                        >
                            <div className="spec-description">
                                {!v2 ? (
                                    <Typography tag="h2" variant="bodyL" className="spec-title">
                                        {title}
                                    </Typography>
                                ) : (
                                    <div className={`spec-title-container${renderIcon ? ' has-icon' : ''}`}>
                                        {renderIcon && (
                                            <DynamicIcon icon={image} alt={`${title} icon`} className="spec-icon" />
                                        )}
                                        <TypographyVisId tag="h2" variant="body1" className="spec-name">
                                            {title}
                                        </TypographyVisId>

                                        {innerWidth < 768 && description && (
                                            <Tooltip
                                                className="spec-tooltip"
                                                size="tiny"
                                                title={title}
                                                content={
                                                    <span dangerouslySetInnerHTML={Helpers.createMarkup(description)} />
                                                }
                                            />
                                        )}
                                        {(promoMessage || description) && (
                                            <div className="spec-messaging">
                                                {promoMessage && (
                                                    <TypographyVisId
                                                        tag="p"
                                                        variant="footnotes"
                                                        className="spec-promo-message"
                                                        weight={700}
                                                    >
                                                        {promoMessage}
                                                    </TypographyVisId>
                                                )}
                                                {description && (
                                                    <TypographyVisId className="spec-desc" tag="p" variant="body1">
                                                        <span
                                                            dangerouslySetInnerHTML={Helpers.createMarkup(description)}
                                                        />
                                                    </TypographyVisId>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {!v2 && promoMessage && (
                                    <Violator small value={promoMessage} variation="offers" style={violatorStyle} />
                                )}
                                {!v2 && device !== 'mobile' && (
                                    <Typography
                                        variant="bodyS"
                                        className="cust-html"
                                        dangerouslySetInnerHTML={Helpers.createMarkup(description)}
                                    />
                                )}

                                {guidingText &&
                                    (!v2 ? (
                                        <Violator
                                            className={`guiding-text cust-html${css ? ` ${css}-product-violator` : ''}`}
                                            variation="product"
                                        >
                                            <span dangerouslySetInnerHTML={Helpers.createMarkup(guidingText)} />
                                        </Violator>
                                    ) : (
                                        <Tag>
                                            <span dangerouslySetInnerHTML={Helpers.createMarkup(guidingText)} />
                                        </Tag>
                                    ))}
                                <AlertMessage type="error" message={errorText} altIcon />
                                <AlertMessage type="warn" message={warningText} altIcon />
                                <AlertMessage type="info" message={infoText} />
                            </div>
                            {type === 'radio' ? (
                                <ConfigureRadio
                                    options={optionitems}
                                    specIndex={i}
                                    hasNoneSelection={hasNoneSelection}
                                    ctoProductInitial={ctoProductInitial}
                                    ctoPrice={ctoPrice}
                                    trackCustomMetric={trackCustomMetric}
                                    path={path}
                                    baseConfig={baseConfig}
                                    GMPoints={GMPoints}
                                    columnSize={columnSize}
                                    variant="secondary"
                                    inputVariant={inputVariant}
                                    colorSwatchProps={colorSwatchProps}
                                    v2={v2}
                                />
                            ) : (
                                <ConfigureCheckbox
                                    options={optionitems}
                                    ctoProductInitial={ctoProductInitial}
                                    specIndex={i}
                                    ctoPrice={ctoPrice}
                                    trackCustomMetric={trackCustomMetric}
                                    GMPoints={GMPoints}
                                />
                            )}
                        </div>
                    ) : null;
                })}
            </div>
        );
    },
    { components: ['baseConfig', 'ctoProductInitial', 'ctoPrice', 'GMPoints', 'ctoSettings'], device: true },
);
